import React, { useCallback, useRef } from "react";
import type { ContentCarouselBlock } from "@/types/content";
import { useCarousel } from "@/hooks/useCarousel";
import { SlideRenderer } from "./SlideRenderer";
import styles from "./ContentCarousel.module.css";

export interface ContentCarouselProps {
  block: ContentCarouselBlock;
  className?: string;
}

/** Left-pointing chevron arrow */
const ArrowLeft: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

/** Right-pointing chevron arrow */
const ArrowRight: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

/**
 * Content Carousel - full-width carousel with varying slide layouts.
 *
 * Supports three slide types:
 * - Intro: centered text with optional logo and CTA
 * - Text + Image: 6/6 grid with configurable image position
 * - Full Image: background image with text overlay
 *
 * Features: keyboard navigation (arrow keys), dot indicators, prev/next arrows.
 */
export const ContentCarousel: React.FC<ContentCarouselProps> = ({
  block,
  className,
}) => {
  const { slides } = block;
  const sectionRef = useRef<HTMLElement>(null);

  const {
    currentIndex,
    goTo,
    goPrev,
    goNext,
    canGoPrev,
    canGoNext,
    translateX,
  } = useCarousel({ itemCount: slides.length });

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    },
    [goPrev, goNext]
  );

  if (!slides || slides.length === 0) {
    return null;
  }

  const containerClasses = [styles.section, className]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      ref={sectionRef}
      className={containerClasses}
      data-component="content-carousel"
      aria-label="Content carousel"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {/* Decorative wave pattern is applied via CSS background-image */}

      <div className={styles.container}>
        {/* Carousel viewport */}
        <div className={styles.carouselViewport}>
          <div
            className={styles.carouselTrack}
            style={{ transform: `translateX(${translateX})` }}
            aria-live="polite"
          >
            {slides.map((slide, index) => (
              <div
                key={slide.id || index}
                className={styles.slide}
                role="group"
                aria-roledescription="slide"
                aria-label={`Slide ${index + 1} of ${slides.length}`}
                aria-hidden={index !== currentIndex}
              >
                <SlideRenderer slide={slide} index={index} />
              </div>
            ))}
          </div>
        </div>

        {/* Navigation controls */}
        <div className={styles.controls}>
          <div className={styles.arrows}>
            <button
              className={styles.arrowButton}
              onClick={goPrev}
              disabled={!canGoPrev}
              aria-label="Previous slide"
            >
              <ArrowLeft />
            </button>
            <button
              className={styles.arrowButton}
              onClick={goNext}
              disabled={!canGoNext}
              aria-label="Next slide"
            >
              <ArrowRight />
            </button>
          </div>

          <div
            className={styles.dots}
            role="tablist"
            aria-label="Slide navigation"
          >
            {slides.map((slide, index) => (
              <button
                key={slide.id || `dot-${index}`}
                className={`${styles.dot} ${index === currentIndex ? styles.dotActive : ""}`}
                onClick={() => goTo(index)}
                role="tab"
                aria-selected={index === currentIndex}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContentCarousel;
